/**
 * Device Stream Manager
 * 단일 디바이스의 모든 채널 스트림을 관리
 * 채널 변경, 연결 풀, 상태 관리를 담당
 */

import { StreamConnectionPool } from './StreamConnectionPool';
import type { MultiDeviceWebRTCConnection } from './WebRTCConnection';
import type {
  CameraChannel,
  StreamQuality,
  StreamType,
  ConnectionState,
  DeviceStats,
  DeviceManagerEvents,
  StreamCallback,
  StreamUpdateEvent,
  VoiceCommState,
  TwoWayAudioStatus,
  VoiceCommCallback
} from './types';

/**
 * Device manager configuration interface
 */
export interface DeviceManagerConfig {
  initialChannel: CameraChannel;
  defaultQuality: StreamQuality;
  enableConnectionPooling: boolean;
  connectionTimeout: number;
  enableVoiceCommSupport: boolean;
}

/**
 * Device Stream Manager Class
 * 단일 디바이스의 모든 채널과 스트림을 관리하는 매니저
 */
export class DeviceStreamManager {
  // Device identification
  private psn: string;
  private config: DeviceManagerConfig;
  
  // Channel management
  private currentChannel: CameraChannel;
  private availableChannels: CameraChannel[] = ['front', 'rear', 'interior', 'exterior'];
  
  // Connection management
  private connectionPool: StreamConnectionPool;
  private activeConnection: MultiDeviceWebRTCConnection | null = null;
  private previewConnections: Map<CameraChannel, MultiDeviceWebRTCConnection> = new Map();
  
  // Stream management
  private currentStream: MediaStream | null = null;
  private streamCallbacks: Set<StreamCallback> = new Set();
  
  // Voice communication management
  private voiceCommState: VoiceCommState;
  private voiceCommCallbacks: Set<VoiceCommCallback> = new Set();
  
  // State tracking
  private isInitialized: boolean = false;
  private connectionStates: Record<CameraChannel, ConnectionState> = {
    front: 'idle',
    rear: 'idle',
    interior: 'idle',
    exterior: 'idle'
  };
  
  // Statistics
  private stats: DeviceStats;
  private lastChannelSwitch: number = 0;
  private totalChannelSwitches: number = 0;
  
  // Event callbacks
  private events: DeviceManagerEvents = {};

  /**
   * Create Device Stream Manager instance
   * @param psn - Device PSN
   * @param config - Device manager configuration
   */
  constructor(psn: string, config: DeviceManagerConfig) {
    this.psn = psn;
    this.config = config;
    this.currentChannel = config.initialChannel;
    
    // Create connection pool
    this.connectionPool = new StreamConnectionPool(psn, {
      enablePooling: config.enableConnectionPooling,
      connectionTimeout: config.connectionTimeout
    });
    
    // Initialize voice communication state
    this.voiceCommState = {
      isActive: false,
      isStarting: false,
      status: 'free',
      error: null,
      localAudioEnabled: true,
      remoteAudioVolume: 1.0,
      localAudioStream: null,
      remoteAudioStream: null
    };
    
    // Initialize statistics
    this.stats = {
      psn: this.psn,
      activeConnections: 0,
      bandwidthUsage: 0,
      memoryUsage: 0,
      currentChannel: this.currentChannel,
      connectionStates: { ...this.connectionStates },
      lastChannelSwitch: 0,
      totalChannelSwitches: 0,
      voiceCommState: { ...this.voiceCommState }
    };
  }

  /**
   * Initialize the device manager
   * 디바이스 매니저 초기화
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    try {
      // Initialize connection pool
      await this.connectionPool.initialize();
      
      this.isInitialized = true;
      
    } catch (error) {
      const errorMessage = `Failed to initialize ${this.psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.events.onError?.(this.psn, new Error(errorMessage));
      throw new Error(errorMessage);
    }
  }

  /**
   * Connect main stream for current or specified channel
   * 메인 스트림 연결 (고품질)
   * @param channel - Channel to connect (default: current channel)
   * @returns MediaStream for the connected channel
   */
  async connectMainStream(channel: CameraChannel = this.currentChannel): Promise<MediaStream> {
    if (!this.isInitialized) {
      throw new Error(`Device ${this.psn} not initialized`);
    }
    
    try {
      // If already connected to the same channel, return current stream
      if (this.activeConnection && this.currentChannel === channel && this.currentStream) {
        return this.currentStream;
      }
      
      // Disconnect existing main connection if different channel
      if (this.activeConnection && this.currentChannel !== channel) {
        await this.demoteMainToPreview();
      }
      
      // Get high-quality connection from pool
      this.updateConnectionState(channel, 'connecting');
      this.activeConnection = await this.connectionPool.getConnection(channel, 'high');
      
      // Wait for stream
      this.currentStream = await this.waitForStream(this.activeConnection);
      this.currentChannel = channel;
      
      // Update state
      this.updateConnectionState(channel, 'connected');
      this.updateStats();
      
      // Notify stream update
      this.notifyStreamUpdate(channel, this.currentStream, 'main', 'high');
      
      return this.currentStream;
      
    } catch (error) {
      this.updateConnectionState(channel, 'failed');
      const errorMessage = `Failed to connect main stream for ${this.psn}/${channel}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.events.onError?.(this.psn, new Error(errorMessage));
      throw new Error(errorMessage);
    }
  }

  /**
   * Switch to a different channel
   * 다른 채널로 변경
   * @param newChannel - New channel to switch to
   * @returns MediaStream for the new channel
   */
  async switchChannel(newChannel: CameraChannel): Promise<MediaStream> {
    if (!this.availableChannels.includes(newChannel)) {
      throw new Error(`Invalid channel: ${newChannel}. Available channels: ${this.availableChannels.join(', ')}`);
    }
    
    if (newChannel === this.currentChannel) {
      return this.currentStream!;
    }
    
    try {
      const oldChannel = this.currentChannel;
      
      // Use FleetA API to change channel (this changes the device's stream output)
      await this.connectionPool.changeDeviceChannel(newChannel);
      
      // Update current channel
      this.currentChannel = newChannel;
      this.lastChannelSwitch = Date.now();
      this.totalChannelSwitches++;
      
      // The existing connection should now receive the new channel's stream
      // No need to create new connection, just wait for stream to update
      if (this.activeConnection) {
        this.currentStream = await this.waitForStream(this.activeConnection);
        this.notifyStreamUpdate(newChannel, this.currentStream, 'main', 'high');
      }
      
      // Update stats and notify
      this.updateStats();
      this.events.onChannelChanged?.(this.psn, oldChannel, newChannel);
      return this.currentStream!;
      
    } catch (error) {
      const errorMessage = `Channel switch failed for ${this.psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.events.onError?.(this.psn, new Error(errorMessage));
      throw new Error(errorMessage);
    }
  }

  /**
   * Add a preview stream for a different channel
   * 다른 채널의 프리뷰 스트림 추가 (저품질)
   * @param channel - Channel to add as preview
   * @returns MediaStream for the preview channel
   */
  async addPreviewStream(channel: CameraChannel): Promise<MediaStream> {
    if (channel === this.currentChannel) {
      throw new Error(`Cannot create preview for current main channel: ${channel}`);
    }
    
    if (this.previewConnections.has(channel)) {
      const connection = this.previewConnections.get(channel)!;
      const stream = await this.waitForStream(connection);
      return stream;
    }
    
    try {
      // Get low-quality connection for preview
      this.updateConnectionState(channel, 'connecting');
      const connection = await this.connectionPool.getConnection(channel, 'low');
      
      // Wait for stream
      const stream = await this.waitForStream(connection);
      
      // Store preview connection
      this.previewConnections.set(channel, connection);
      this.updateConnectionState(channel, 'connected');
      
      // Notify stream update
      this.notifyStreamUpdate(channel, stream, 'preview', 'low');
      
      return stream;
      
    } catch (error) {
      this.updateConnectionState(channel, 'failed');
      const errorMessage = `Failed to add preview stream for ${this.psn}/${channel}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.events.onError?.(this.psn, new Error(errorMessage));
      throw new Error(errorMessage);
    }
  }

  /**
   * Start voice communication
   * 음성 통신 시작
   */
  async startVoiceComm(): Promise<TwoWayAudioStatus> {
    if (!this.config.enableVoiceCommSupport) {
      throw new Error('Voice communication not enabled for this device');
    }
    
    if (this.voiceCommState.isActive) {
      return this.voiceCommState.status;
    }
    
    if (!this.activeConnection) {
      throw new Error('No active connection for voice communication');
    }
    
    try {
      // Update state to starting
      this.updateVoiceCommState({ isStarting: true, error: null });
      
      // Start voice communication on active connection
      const status = await this.activeConnection.startVoiceComm();
      
      // Update state to active
      this.updateVoiceCommState({
        isActive: true,
        isStarting: false,
        status: 'active',
        error: null
      });
      
      return status;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start voice communication';
      
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'error',
        error: errorMessage
      });
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Stop voice communication
   * 음성 통신 중지
   */
  async stopVoiceComm(): Promise<TwoWayAudioStatus> {
    if (!this.voiceCommState.isActive) {
      return 'free';
    }
    
    if (!this.activeConnection) {
      return 'free';
    }
    
    try {
      // Stop voice communication on active connection
      const status = await this.activeConnection.stopVoiceComm();
      
      // Update state
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'free',
        error: null,
        localAudioStream: null,
        remoteAudioStream: null
      });
      
      return status;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop voice communication';
      
      // Force reset state even on error
      this.updateVoiceCommState({
        isActive: false,
        isStarting: false,
        status: 'error',
        error: errorMessage
      });
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Toggle local audio (microphone mute/unmute)
   */
  setLocalAudioEnabled(enabled: boolean): void {
    if (!this.activeConnection) {
      return;
    }
    
    this.activeConnection.setLocalAudioEnabled(enabled);
    this.updateVoiceCommState({ localAudioEnabled: enabled });
  }
  
  /**
   * Set remote audio volume
   */
  setRemoteAudioVolume(volume: number): void {
    if (!this.activeConnection) {
      return;
    }
    
    this.activeConnection.setRemoteAudioVolume(volume);
    this.updateVoiceCommState({ remoteAudioVolume: volume });
  }
  
  /**
   * Get voice communication state
   */
  getVoiceCommState(): VoiceCommState {
    return { ...this.voiceCommState };
  }
  
  /**
   * Disconnect from all streams
   * 모든 스트림 연결 해제
   */
  async disconnect(): Promise<void> {
    try {
      // Stop voice communication if active
      if (this.voiceCommState.isActive) {
        await this.stopVoiceComm();
      }
      
      // Disconnect main connection
      if (this.activeConnection) {
        await this.activeConnection.disconnect();
        this.activeConnection = null;
        this.currentStream = null;
      }
      
      // Disconnect all preview connections
      for (const [channel, connection] of this.previewConnections) {
        await connection.disconnect();
      }
      this.previewConnections.clear();
      
      // Reset all connection states
      Object.keys(this.connectionStates).forEach(channel => {
        this.connectionStates[channel as CameraChannel] = 'disconnected';
      });
      
      // Update stats
      this.updateStats();
      
    } catch (error) {
    }
  }

  /**
   * Get available channels for this device
   * 이 디바이스의 사용 가능한 채널 목록
   * @returns Array of available channels
   */
  getAvailableChannels(): CameraChannel[] {
    return [...this.availableChannels];
  }

  /**
   * Get current active channel
   * 현재 활성 채널 조회
   * @returns Current channel
   */
  getCurrentChannel(): CameraChannel {
    return this.currentChannel;
  }

  /**
   * Get device PSN
   * 디바이스 PSN 조회
   * @returns Device PSN
   */
  getPSN(): string {
    return this.psn;
  }

  /**
   * Get connection state for a specific channel
   * 특정 채널의 연결 상태 조회
   * @param channel - Channel to check
   * @returns Connection state
   */
  getConnectionState(channel?: CameraChannel): ConnectionState {
    if (channel) {
      return this.connectionStates[channel];
    }
    return this.connectionStates[this.currentChannel];
  }

  /**
   * Get current main stream
   * 현재 메인 스트림 조회
   * @returns Current MediaStream or null
   */
  getCurrentStream(): MediaStream | null {
    return this.currentStream;
  }

  /**
   * Get device statistics
   * 디바이스 통계 조회
   * @returns Device statistics
   */
  getStats(): DeviceStats {
    return { ...this.stats };
  }

  /**
   * Register stream update callback
   * 스트림 업데이트 콜백 등록
   * @param callback - Stream update callback function
   */
  onStreamUpdate(callback: StreamCallback): void {
    this.streamCallbacks.add(callback);
  }

  /**
   * Unregister stream update callback
   * 스트림 업데이트 콜백 해제
   * @param callback - Stream update callback function to remove
   */
  offStreamUpdate(callback: StreamCallback): void {
    this.streamCallbacks.delete(callback);
  }

  /**
   * Register voice communication callback
   * 음성 통신 콜백 등록
   * @param callback - Voice communication callback function
   */
  onVoiceCommStateChange(callback: VoiceCommCallback): void {
    this.voiceCommCallbacks.add(callback);
  }
  
  /**
   * Unregister voice communication callback
   * 음성 통신 콜백 해제
   * @param callback - Voice communication callback function to remove
   */
  offVoiceCommStateChange(callback: VoiceCommCallback): void {
    this.voiceCommCallbacks.delete(callback);
  }
  
  /**
   * Set event callbacks
   * 이벤트 콜백 설정
   * @param events - Event callback functions
   */
  setEvents(events: DeviceManagerEvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Destroy device manager and cleanup all resources
   * 디바이스 매니저 파괴 및 모든 리소스 정리
   */
  async destroy(): Promise<void> {
    try {
      // Disconnect all streams
      await this.disconnect();
      
      // Destroy connection pool
      await this.connectionPool.destroy();
      
      // Clear callbacks
      this.streamCallbacks.clear();
      this.voiceCommCallbacks.clear();
      this.events = {};
      
      // Reset state
      this.isInitialized = false;
      this.currentStream = null;
      this.activeConnection = null;
      
    } catch (error) {
    }
  }

  // ========================
  // PRIVATE METHODS
  // ========================

  /**
   * Wait for MediaStream from a WebRTC connection
   * WebRTC 연결에서 MediaStream 대기
   * @param connection - WebRTC connection
   * @returns Promise that resolves with MediaStream
   */
  private async waitForStream(connection: MultiDeviceWebRTCConnection): Promise<MediaStream> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Stream timeout for ${this.psn}`));
      }, this.config.connectionTimeout);
      
      const checkStream = () => {
        const stream = connection.getStream();
        if (stream) {
          clearTimeout(timeout);
          resolve(stream);
        } else {
          setTimeout(checkStream, 100); // Check every 100ms
        }
      };
      
      checkStream();
    });
  }

  /**
   * Demote main connection to preview
   * 메인 연결을 프리뷰로 강등
   */
  private async demoteMainToPreview(): Promise<void> {
    if (!this.activeConnection) return;
    
    const currentChannel = this.currentChannel;
    
    // Reduce quality of current connection to low
    await this.activeConnection.changeQuality('low');
    
    // Move to preview connections
    this.previewConnections.set(currentChannel, this.activeConnection);
    
    // Clear main connection
    this.activeConnection = null;
    this.currentStream = null;
  }

  /**
   * Update connection state for a channel
   * 채널의 연결 상태 업데이트
   * @param channel - Channel to update
   * @param state - New connection state
   */
  private updateConnectionState(channel: CameraChannel, state: ConnectionState): void {
    const oldState = this.connectionStates[channel];
    this.connectionStates[channel] = state;
    
    if (oldState !== state) {
      this.events.onConnectionStateChange?.(this.psn, channel, state);
    }
  }

  /**
   * Update device statistics
   * 디바이스 통계 업데이트
   */
  private updateStats(): void {
    const activeConnections = (this.activeConnection ? 1 : 0) + this.previewConnections.size;
    
    this.stats = {
      psn: this.psn,
      activeConnections,
      bandwidthUsage: this.calculateBandwidthUsage(),
      memoryUsage: this.calculateMemoryUsage(),
      currentChannel: this.currentChannel,
      connectionStates: { ...this.connectionStates },
      lastChannelSwitch: this.lastChannelSwitch,
      totalChannelSwitches: this.totalChannelSwitches,
      voiceCommState: { ...this.voiceCommState }
    };
    
    this.events.onStatsUpdate?.(this.psn, this.stats);
  }

  /**
   * Calculate bandwidth usage
   * 대역폭 사용량 계산
   * @returns Estimated bandwidth usage in bytes/sec
   */
  private calculateBandwidthUsage(): number {
    let bandwidth = 0;
    
    // Main stream (high quality)
    if (this.activeConnection) {
      bandwidth += 5000000 / 8; // 5Mbps to bytes/sec
    }
    
    // Preview streams (low quality)
    bandwidth += this.previewConnections.size * (1000000 / 8); // 1Mbps each
    
    return bandwidth;
  }

  /**
   * Calculate memory usage
   * 메모리 사용량 계산
   * @returns Estimated memory usage in bytes
   */
  private calculateMemoryUsage(): number {
    let memory = 50 * 1024 * 1024; // Base 50MB for device manager
    
    // Add connection memory
    memory += (this.activeConnection ? 1 : 0) * 20 * 1024 * 1024; // 20MB per connection
    memory += this.previewConnections.size * 10 * 1024 * 1024; // 10MB per preview
    
    return memory;
  }

  /**
   * Notify stream update to all callbacks
   * 모든 콜백에 스트림 업데이트 알림
   * @param channel - Updated channel
   * @param stream - Updated MediaStream
   * @param type - Stream type
   * @param quality - Stream quality
   */
  private notifyStreamUpdate(channel: CameraChannel, stream: MediaStream, type: StreamType, quality: StreamQuality): void {
    const event: StreamUpdateEvent = {
      psn: this.psn,
      channel,
      stream,
      type,
      quality,
      timestamp: Date.now()
    };
    
    this.streamCallbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error(`❌ [DeviceStreamManager] Stream callback error for ${this.psn}:`, error);
      }
    });
    
    // Also notify device-level event
    this.events.onStreamUpdate?.(event);
  }
  
  /**
   * Update voice communication state and notify callbacks
   * 음성 통신 상태 업데이트 및 콜백 알림
   * @param updates - Partial voice communication state updates
   */
  private updateVoiceCommState(updates: Partial<VoiceCommState>): void {
    this.voiceCommState = { ...this.voiceCommState, ...updates };
    
    // Update stats
    this.updateStats();
    
    // Notify all voice comm callbacks
    this.voiceCommCallbacks.forEach(callback => {
      try {
        callback(this.psn, this.voiceCommState);
      } catch (error) {
        console.error(`❌ [DeviceStreamManager] Voice comm callback error for ${this.psn}:`, error);
      }
    });
    
    // Also notify device-level event
    this.events.onVoiceCommStateChange?.(this.psn, this.voiceCommState);
  }
}